import {
  allowedTransitions,
  TRANSITIONABLE_USER_STATUSES,
  type TransitionableUserStatus,
  type UserStatus,
} from '@aysar/contracts';

const TRANSITIONABLE = new Set<UserStatus>(TRANSITIONABLE_USER_STATUSES);

function isTransitionable(status: UserStatus): status is TransitionableUserStatus {
  return TRANSITIONABLE.has(status);
}

/**
 * The statuses the **generic** status-change endpoint can actually reach from
 * here — i.e. exactly the buttons the portal may offer.
 *
 * Two filters, and the second is not expressible in the transition map.
 *
 * `suspended` and `banned` are dropped because they carry consequences a plain
 * status write does not perform (an end date, a remembered previous status,
 * session revocation, the events P20 listens for). They have their own doors,
 * which is why `TRANSITIONABLE_USER_STATUSES` excludes them.
 *
 * **A suspended account has no targets at all**, though the map lists five. The
 * map is right — a restore may return the member to whichever status they held
 * before — but `UserStatusService.transition` throws `SuspensionExitError`
 * before it ever consults the map, because leaving suspension by any other route
 * would put the account somewhere it was never entitled to. So an intersection
 * with `allowedTransitions` alone believes the map and offers five targets that
 * each answer 422; the `suspended` term is what makes this agree with the server.
 */
export function statusTargets(status: UserStatus): readonly TransitionableUserStatus[] {
  if (status === 'suspended') return [];

  return allowedTransitions(status).filter(isTransitionable);
}
